import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import quarkusApi from '../api/quarkusApi';

// The front uses dayjs weekday numbers (1 = Monday ... 6 = Saturday); the API uses the WeekDay enum
const weekdayIdToWeekDay = {
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
};

const weekDayToWeekdayId = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const toMessage = (data) => (typeof data === 'string' ? data : 'Ocorreu um erro. Tente novamente.');

const mapRecurrentTaskFromApi = (dto) => ({
  id: dto.id,
  title: dto.title,
  description: dto.description || '',
  weekdays: (dto.daysOfWeek || []).map((weekDay) => weekDayToWeekdayId[weekDay]),
  order: dto.order,
});

const toPayload = (recurringForm, order) => ({
  title: recurringForm.title,
  description: recurringForm.description,
  daysOfWeek: recurringForm.weekdays.map((weekdayId) => weekdayIdToWeekDay[weekdayId]),
  order,
});

export default function useRecurrentTasks() {
  const navigate = useNavigate();

  const [recurrentTasks, setRecurrentTasks] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleError = useCallback(
    (returnFromApi) => {
      setShowErrorMessage(true);
      setErrorMessage(toMessage(returnFromApi.response?.data));
      console.error('ops! ocorreu um erro' + returnFromApi);
      if (returnFromApi.status === 401) {
        navigate('/expired-session');
      }
      return false;
    },
    [navigate],
  );

  const fetchRecurrentTasks = useCallback(() => {
    setLoading(true);
    return quarkusApi
      .get('/recurrent-tasks', {
        headers: { Authorization: sessionStorage.getItem('token') },
      })
      .then((returnFromApi) => {
        const mapped = (returnFromApi.data || [])
          .map(mapRecurrentTaskFromApi)
          .sort((a, b) => a.order - b.order);
        setRecurrentTasks(mapped);
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [handleError]);

  const addRecurrentTask = (recurringForm, order) => {
    setLoading(true);
    return quarkusApi
      .post('/recurrent-tasks', toPayload(recurringForm, order), {
        headers: { Authorization: sessionStorage.getItem('token') },
      })
      .then((returnFromApi) => {
        setSuccessMessage(toMessage(returnFromApi.data));
        setShowSuccessMessage(true);
        return true;
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  const updateRecurrentTask = (id, recurringForm, order) => {
    setLoading(true);
    return quarkusApi
      .put(`/recurrent-tasks/${id}`, toPayload(recurringForm, order), {
        headers: { Authorization: sessionStorage.getItem('token') },
      })
      .then((returnFromApi) => {
        setSuccessMessage(toMessage(returnFromApi.data));
        setShowSuccessMessage(true);
        return true;
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  const changeRecurrentTaskOrder = (id, order) => {
    setLoading(true);
    return quarkusApi
      .put(
        `/recurrent-tasks/${id}/order`,
        { order },
        { headers: { Authorization: sessionStorage.getItem('token') } },
      )
      .then(() => true)
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  const deleteRecurrentTask = (id) => {
    setLoading(true);
    return quarkusApi
      .delete(`/recurrent-tasks/${id}`, {
        headers: { Authorization: sessionStorage.getItem('token') },
      })
      .then((returnFromApi) => {
        setSuccessMessage(toMessage(returnFromApi.data));
        setShowSuccessMessage(true);
        return true;
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  return {
    recurrentTasks,
    isLoading,
    showSuccessMessage,
    setShowSuccessMessage,
    successMessage,
    showErrorMessage,
    setShowErrorMessage,
    errorMessage,
    fetchRecurrentTasks,
    addRecurrentTask,
    updateRecurrentTask,
    changeRecurrentTaskOrder,
    deleteRecurrentTask,
  };
}
